import React, { memo, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { variationsList, variationCategoriesList } from "../../services/exercise_collections";

import { useLocalStorage } from 'usehooks-ts'
import Pagination from "../../components/shared/pagination";
import VariationContentPreview from "../../components/variation_content_preview";
import { SortBar, SearchBar } from "../../components/FilterBar";
import FiltersModalButton from "../../components/FiltersModalButton";

export const VariationsListPage = () => {
  const navigate = useNavigate();

  const add = () => navigate("/club_admin/exercise_collections/new");
  const select = (item) => navigate(`/club_admin/exercise_collections/${item.id}`);

  return <VariationsList select={select} add={add}/>
}

export default memo(VariationsListPage);

export const VariationsList = ({ select, add }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [variations, setVariations] = useState([])
  const [categories, setCategories] = useState([])
  const [count, setCount] = useState(0)

  const getFilterValue = (param, defaultValue) => searchParams.get(param) || defaultValue;
  const initialFilters = {
    page: Number(getFilterValue("page", 1)),
    per_page: 10,
    owner_type: getFilterValue("owner_type", "club"),
    sort_dir: getFilterValue("sort_dir", 'asc'),
    sort_by: 'title',
    q: getFilterValue("q", ""),
    category_ids: getFilterValue("category_ids", null),
    from_age: getFilterValue("from_age", null),
    to_age: getFilterValue("to_age", null)
  };
  const [filters, setFilters] = useState(initialFilters);
  const { owner_type } = filters;

  const [viewType] = useLocalStorage('variationsViewType', 'list')

  const { t } = useTranslation();

  const updateSearchParams = (newFilters) => {
    const updatedParams = new URLSearchParams(searchParams);
    
    Object.entries(newFilters).forEach(([key, value]) =>
      value ? updatedParams.set(key, value) : updatedParams.delete(key)
    );
  
    setSearchParams(updatedParams);
  };

  const loadData = async () => {
    const { exercise_collections, count } = await variationsList(filters);
    setVariations(exercise_collections);
    setCount(count);
  };

  useEffect(() => {
    updateSearchParams(filters);
    loadData();
  }, [filters])

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }) }, [filters?.page]);

  useEffect(() => {
    const loadCategories = async () => {
      const data = await variationCategoriesList();
      setCategories(data.categories);
    };

    loadCategories();
  }, [])


  return (
    <div className="teams">
      <div className="teams-header">
        <div className="teams-header-text">{t('exercise_collections.variations')}</div>
        {add && <button className="button button-add pointer" onClick={add}>{t('buttons.variation.add')}</button>}
      </div>
      <div className="team-details-tabs margin-top">
        <div className={`team-details-tabs-option pointer ${owner_type === "club" ? 'active' : ''}`} onClick={() => owner_type !== 'clubs' && setFilters({...filters, owner_type: 'club', page: 1 })}>{t('exercise_collections.club')}</div>
        <div className={`team-details-tabs-option pointer ${owner_type === "supercoach" ? 'active' : ''}`} onClick={() =>  owner_type !== 'supercoach' && setFilters({...filters, owner_type: 'supercoach', page: 1 })}>{t('exercise_collections.supercoach')}</div>
      </div>
      <div className='filters-bar'>
        <div className='flex1'>
          <SortBar viewTypeName='variationsViewType' onSortChange={(sort_dir) => setFilters({...filters, sort_dir })} direction={filters.sort_dir} />
          <SearchBar onChange={(q) => {setFilters({...filters, q})}} placeholder={t('exercise_collections.placeholder')} value={filters.q} />
        </div>
        <FiltersModalButton filters={filters} onChange={(params) => setFilters({...filters, page: 1, ...params})} categories={categories}/>
      </div>
      <div className={`library-list-container ${viewType}-view`}>
        {variations.map(item =>
          viewType === 'list'
            ? <VariationListItem variation={item} key={item.id} select={select}/>
            : <VariationCardItem variation={item} key={item.id} select={select}/>
        )}
      </div>
      <Pagination filters={filters} count={count} onChange={setFilters}/>


    </div>

  )
};

const VariationListItem = ({ variation, select }) => {

  const { t } = useTranslation();

  return (
    <div className="library-item">
      <div className="library-item-preview pointer" onClick={() => { select(variation) }}>
        <VariationContentPreview collection={variation.exercise_collections_exercises_attributes}/>
      </div>
      <div className="library-item-info">
        <div className="flex-between1">
          <div className="library-item-info-title pointer" onClick={() => { select(variation) }}>{variation.title}</div>
          <div>
            <span className={`new_label new_label-${variation.published ? 'success' : 'default'}`}>
              {variation.published ? t("common.published") : t("common.draft")}
            </span>
          </div>
        </div>
        <div className="library-item-info-row">
          {variation.exercise_collections_exercises_attributes.map(item => item?.title).join(", ")}
        </div>
        <div className="library-item-info-row">
        {t('exercise_collections.age')} {variation.minimum_age} - {variation.maximum_age}
        </div>
        <div className="library-item-info-row">
          <div className="tags-container">
            <div className="tags">
              <div className="mr10">{t('exercise_collections.tags')}:</div>
              {variation.categories.map(category => <button key={category.id} className="button button-category mr10">{category.title}</button>)}
            </div>
          </div>
        </div>
        <div className="library-item-info-row">
          <div className="field-info">
            <img src="/images/icons/field_size.svg" className='field-info-img'/>
            {variation.field_size}
          </div>
        </div>
      </div>
    </div>
  )
}


const VariationCardItem = ({ variation, select }) => {

  return (
    <div className="library-item">
      <div className="library-item-preview pointer" onClick={() => { select(variation) }}>
        <VariationContentPreview collection={variation.exercise_collections_exercises_attributes} size={350}/>
      </div>
      <div className="library-item-info">
        <h5 className="library-item-info-title pointer" onClick={() => { select(variation) }}>
          {variation.title}
        </h5>
      </div>
    </div>
  )
}

